import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import utils from 'utils';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { isDuplicateEmailError } from 'utils/profile';
import { contactInfoPayloadParser } from 'utils/driverProfile';
import { analyticsClearErrors } from 'utils/analytics';
import { isObjectEmpty } from '@ehi/global-marketing-interface/lib/utils/js/object';
import { types } from '@ehi/global-marketing-interface/lib/utils/js/types';
import { Field } from 'react-final-form';
import Form from 'components/Form/Form';
import TextField from 'components/Form/TextField';
import CheckboxField from 'components/Form/Checkbox';
import PasswordField from 'components/Form/PasswordField';
import PasswordRequirements, { customPasswordValidation } from 'components/Form/PasswordRequirements';
import Modal from 'components/Modal';
import { ModalFooter } from 'components/Modal/ModalFooter';
import Anchor from 'components/Anchor';
import Button from 'components/Button';
import ToolTip from 'components/ToolTip';
import PhoneNumberField from 'components/Form/PhoneNumberField';
import ServiceErrors from 'components/ServiceErrors';
import { ANALYTICS, MODAL_THEMES, MODAL_BUTTON_TYPES, THEMES, WINDOW_OBJECT_KEYS, PROFILE } from 'constants';

const { ACCEPT, DECLINE } = MODAL_BUTTON_TYPES;
const { PREFERENCE_SOURCE_CODE } = PROFILE;

/**
 * EditAccountModal
 * in this modal the user can update their username, email address, special offers subscription, and password
 *
 * @param {object} props - React Props
 * @param {function} props.cleanMessages - clears the redux gmi error messaging
 * @param {function} props.closeModal
 * @param {boolean} props.isOpen - whether or not the modal is visible
 * @param {string} props.modalKey - string identifier for the modal
 * @param {function} props.modifyProfile - makes the request with new form values
 * @param {object} props.profileData
 * @param {string} props.supportPhone - country specific support number
 * @return {JSX}
 */

const EditAccountModal = ({
  cleanMessages,
  closeEditAccountModal,
  isOpen,
  modalKey,
  modifyProfile,
  profileData,
  supportPhone,
  openEmailInUseModal,
  hasServiceError,
}) => {
  const [initialState, setInitialState] = useState({
    isPasswordValid: false,
    showModifyPassword: false,
  });

  const resetState = () => {
    setInitialState({
      isPasswordValid: false,
      showModifyPassword: false,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleCleanMessages = useCallback(() => {
    cleanMessages();
    analyticsClearErrors();
  }, [cleanMessages]);

  const handleClose = useCallback(() => {
    closeEditAccountModal({ analyticsValue: ANALYTICS.SAVE });
    handleCleanMessages();
  }, [closeEditAccountModal, handleCleanMessages]);

  const handleSave = async (values) => {
    analyticsClearErrors();
    const { email, password, special_offers, user_name, phones } = values;

    const payload = {};
    const emailChanged = email !== profileData.email;
    const userNameChanged = user_name !== profileData.user_name;
    const phoneNumberChanged = phones?.[0]?.phone_number !== profileData.phones?.[0]?.phone_number;

    const keepEmailUsernameEqual = profileData.email === profileData.user_name;

    if (emailChanged || userNameChanged || phoneNumberChanged) {
      if (keepEmailUsernameEqual) {
        payload.contact = { email };
        payload.user_name = email;
      } else {
        if (emailChanged) payload.contact = { email };
        if (userNameChanged) payload.user_name = user_name;
      }

      if (phoneNumberChanged) {
        const phoneData = contactInfoPayloadParser(values);
        payload.contact = {
          ...payload.contact,
          phones: phoneData.phones,
        };
      }
    }

    if (password) {
      payload.password = { new_password: password };
    }

    if (
      special_offers !== profileData.special_offers ||
      (profileData?.special_offers && special_offers === profileData.special_offers && emailChanged)
    ) {
      payload.preference = {
        email_preference: { special_offers },
      };
      if (special_offers) {
        payload.preference.source_code = PREFERENCE_SOURCE_CODE.AI_PROFILE;
      }
    }

    if (!isObjectEmpty(payload)) {
      const res = await modifyProfile(payload);
      if (res.messages) {
        const isEmailAlreadyInUse = res.messages.some((msg) => isDuplicateEmailError(msg.code));
        if (isEmailAlreadyInUse) {
          closeEditAccountModal({ skipAnalytics: true });
          openEmailInUseModal();
        }
      } else {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const handleToggleShowModifyPassword = (form) => () => {
    setInitialState((prev) => ({
      ...prev,
      showModifyPassword: !prev.showModifyPassword,
    }));
    form.change('password', '');
  };

  const checkboxLabel = (fieldState) => {
    let { value } = fieldState || {};
    if (!types(value).isDefined) {
      value = profileData?.special_offers;
    }

    return (
      <p>
        <span>{i18n('edit_account_modal_label_checkbox')}</span>
        <span className='edit-account-modal__sign-up__success'>
          {' '}
          {i18n(value ? 'edit_account_modal_label_checkbox_selected' : 'edit_account_modal_label_checkbox_unselected')}
        </span>
      </p>
    );
  };

  const { first_name, last_name, email, special_offers, user_name, phones, email_update_required } = profileData;
  const showUserName = user_name && !email_update_required && user_name !== email;

  if (email_update_required) {
    profileData.email = '';
  }

  return (
    <Modal
      modalKey={modalKey}
      theme={MODAL_THEMES.WHITE}
      header={i18n('edit_account_modal_title')}
      className='edit-account-modal'
      customActionOnClose={handleCleanMessages}
    >
      <Form
        onSubmit={handleSave}
        initialValues={profileData}
        validate={initialState.showModifyPassword ? customPasswordValidation(initialState.isPasswordValid) : null}
        subscription={{ submitting: true, pristine: true, submitFailed: true }}
      >
        {({ form, handleSubmit }) => (
          <form className='edit-account-modal__form' onSubmit={handleSubmit}>
            <ServiceErrors />
            <div className='edit-account-modal__section-header'>
              <span className='edit-account-modal__required-note'>{i18n('header_note_required_field')}</span>
            </div>
            <div>
              <p className='edit-account-modal__display-label'>
                {i18n('edit_account_modal_label_name')}
                <ToolTip
                  placement='top-start'
                  triggerTitle={i18n('discount_code_tooltip_trigger_label')}
                  analyticsKey='name'
                >
                  <label>
                    {i18n('edit_account_modal_label_name_tooltip', [<strong key='0'>{supportPhone}</strong>], {
                      jsx: true,
                    })}
                  </label>
                </ToolTip>
              </p>
              <p>{`${first_name} ${last_name}`}</p>
            </div>

            {showUserName && (
              <div className='edit-account-modal__field-row'>
                <TextField
                  initialValueButton={user_name}
                  name='user_name'
                  label={i18n('edit_account_modal_label_username')}
                  required
                />
              </div>
            )}

            <div className='edit-account-modal__field-row'>
              <TextField
                initialValueButton={email_update_required ? '' : email}
                name='email'
                id='email'
                label={i18n('edit_account_modal_label_email')}
                validations={[utils.fieldValidation.email]}
                required
                forceError={hasServiceError}
              />
              <PhoneNumberField
                initialValueButton={phones?.[0]?.phone_number}
                name='phones[0].phone_number'
                label={i18n('common_phone_number')}
                fill
                required
                validations={[utils.phoneInput.phone, utils.phoneInput.checkPhoneCode]}
                autoComplete='phone'
              />
            </div>

            <div className='edit-account-modal__field-row'>
              <CheckboxField
                name='special_offers'
                id='special_offers'
                label={checkboxLabel(form.getFieldState('special_offers'))}
                theme={THEMES.LIGHT}
                initialValue={special_offers}
              />
            </div>

            <span className='edit-account-modal__sign-up__legal'>
              {i18n(
                'edit_account_modal_label_checkbox_details',
                {
                  0: (
                    <Anchor href={config.getUrl(WINDOW_OBJECT_KEYS.PRIVACY_POLICY)} key={0} isExternal>
                      {i18n('edit_account_modal_privacy_policy')}
                    </Anchor>
                  ),
                  1: (
                    <Anchor href={config.getUrl(WINDOW_OBJECT_KEYS.COOKIE_POLICY)} key={1} isExternal>
                      {i18n('edit_account_modal_cookie_policy')}
                    </Anchor>
                  ),
                },
                { jsx: true }
              )}
            </span>

            {initialState.showModifyPassword ? (
              <>
                <p className='edit-account-modal__display-label'>
                  {i18n('edit_account_modal_label_password_modify')}
                  <Button
                    link
                    linkText
                    className='edit-account-modal__password-cancel'
                    onClick={handleToggleShowModifyPassword(form)}
                    data-dtm-track={utils.analytics.dtm(
                      ANALYTICS.PROFILE,
                      'account_information',
                      'cancel_modify_password'
                    )}
                  >
                    {i18n('edit_account_modal_label_password_cancel')}
                  </Button>
                </p>
                <Field component='input' type='hidden' name='first_name' id='first_name' />
                <Field component='input' type='hidden' name='last_name' id='last_name' />

                <div className='edit-account-modal__field-row'>
                  <PasswordField
                    name='password'
                    label={i18n('edit_account_modal_new_password')}
                    required
                    autoComplete='new-password'
                  />
                </div>

                <PasswordRequirements
                  password='password'
                  firstName='first_name'
                  lastName='last_name'
                  setPasswordValid={(val) => setInitialState((prev) => ({ ...prev, isPasswordValid: val }))}
                />
              </>
            ) : (
              <>
                <p className='edit-account-modal__display-label'>{i18n('edit_account_modal_label_password')}</p>
                <Button
                  link
                  linkText
                  onClick={handleToggleShowModifyPassword(form)}
                  data-dtm-track={utils.analytics.dtm(ANALYTICS.PROFILE, 'account_information', 'modify_password')}
                >
                  {i18n('edit_account_modal_label_password_modify')}
                </Button>
              </>
            )}

            <ModalFooter
              buttons={[
                {
                  type: DECLINE,
                  label: i18n('edit_account_modal_cancel'),
                  analyticsValue: ANALYTICS.CANCEL,
                  handler: () => {
                    handleCleanMessages();
                    closeEditAccountModal({ analyticsValue: ANALYTICS.CANCEL });
                  },
                },
                {
                  type: ACCEPT,
                  label: i18n('edit_account_modal_save'),
                  handler: handleSubmit,
                },
              ]}
            />
          </form>
        )}
      </Form>
    </Modal>
  );
};

EditAccountModal.propTypes = {
  cleanMessages: PropTypes.func,
  closeEditAccountModal: PropTypes.func,
  isOpen: PropTypes.bool,
  modalKey: PropTypes.string,
  modifyProfile: PropTypes.func,
  profileData: PropTypes.object,
  supportPhone: PropTypes.string,
};

export default EditAccountModal;
